/**
 * /impact/dashboard — Global Impact Dashboard.
 *
 * Live totals of the entire SocialCapital.cc network, backed by the
 * existing ``/api/social-capital/impact-summary`` endpoint (honest
 * count_documents queries — no marketing inflation).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart, Utensils, Trees, Users, Repeat, BookOpen,
  Sparkles, ArrowRight, Loader2,
} from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";

const METRICS = [
  { key: "people_helped",    label: "People helped",     Icon: Heart,    tint: "#DC2626" },
  { key: "meals_rescued",    label: "Meals rescued",     Icon: Utensils, tint: "#D97706" },
  { key: "trees_planted",    label: "Trees planted",     Icon: Trees,    tint: "#059669" },
  { key: "volunteer_events", label: "Volunteer events",  Icon: Users,    tint: "#2563EB" },
  { key: "items_reused",     label: "Items reused",      Icon: Repeat,   tint: "#7C3AED" },
  { key: "knowledge_shared", label: "Knowledge shared",  Icon: BookOpen, tint: "#0891B2" },
];

function fmtNumber(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (v >= 1_000)     return (v / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return v.toLocaleString();
}

export default function SocialCapitalImpactDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    http.get("/social-capital/impact-summary")
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) { setError(true); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 md:py-14" data-testid="sc-impact-dashboard">
      <SeoHead section="social-capital"
               title="Global Impact Dashboard · SocialCapital.cc"
               description="Live totals of people helped, meals rescued, trees planted, volunteer events, items reused and knowledge shared across the SocialPulse network."
               url="/impact/dashboard" />

      {/* Hero */}
      <section
        className="relative overflow-hidden rounded-3xl p-8 md:p-12 mb-10"
        style={{
          background: "linear-gradient(135deg,#065F46 0%,#059669 55%,#10B981 100%)",
          color: "#F0FDF4",
        }}
      >
        <div
          className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-25 pointer-events-none"
          style={{ background: "radial-gradient(circle,#A7F3D0 0%,transparent 70%)" }}
        />
        <div className="relative z-10">
          <div className="overline text-white/75 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Impact · Live totals
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-bold mt-3">
            Every action, counted.
          </h1>
          <p className="mt-5 max-w-2xl text-white/90 text-base sm:text-lg leading-relaxed">
            These numbers move whenever anyone in the SocialCapital.cc network
            logs a verified contribution. No inflated marketing — just what our
            members have actually done.
          </p>
        </div>
      </section>

      {/* Metric grid */}
      <section
        className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10"
        data-testid="sc-impact-metrics"
      >
        {METRICS.map(({ key, label, Icon, tint }) => (
          <div key={key}
               className="rounded-2xl border p-6 bg-white"
               style={{ borderColor: "rgba(0,0,0,0.06)" }}
               data-testid={`sc-metric-${key}`}>
            <Icon className="w-6 h-6" style={{ color: tint }} />
            <div className="mt-3 text-3xl sm:text-4xl font-display font-bold tabular-nums">
              {loading ? (
                <Loader2 className="w-6 h-6 animate-spin opacity-40" />
              ) : error ? (
                <span className="opacity-50">—</span>
              ) : (
                fmtNumber(data?.[key])
              )}
            </div>
            <div className="mt-1 text-sm opacity-70">{label}</div>
          </div>
        ))}
      </section>

      {/* CTAs */}
      <section className="grid sm:grid-cols-2 gap-4">
        <Link to="/earn/submit"
              className="rounded-2xl border p-6 hover:border-emerald-300 transition-colors bg-white"
              style={{ borderColor: "rgba(0,0,0,0.08)" }}
              data-testid="sc-impact-cta-submit">
          <div className="overline mb-2" style={{ color: "#059669" }}>Add to the total</div>
          <div className="font-display text-xl mb-2">Submit your own action</div>
          <p className="text-sm opacity-70 leading-relaxed">
            Every entry you log — big or small — moves the world's Social
            Capital total upward.
          </p>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold"
               style={{ color: "#059669" }}>
            Log a contribution <ArrowRight className="w-4 h-4" />
          </div>
        </Link>
        <Link to="/impact/stories"
              className="rounded-2xl border p-6 hover:border-emerald-300 transition-colors bg-white"
              style={{ borderColor: "rgba(0,0,0,0.08)" }}
              data-testid="sc-impact-cta-stories">
          <div className="overline mb-2" style={{ color: "#059669" }}>The stories behind the numbers</div>
          <div className="font-display text-xl mb-2">Read member stories</div>
          <p className="text-sm opacity-70 leading-relaxed">
            The real humans whose contributions built these totals.
          </p>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold"
               style={{ color: "#059669" }}>
            Explore stories <ArrowRight className="w-4 h-4" />
          </div>
        </Link>
      </section>

      {data?.generated_at && (
        <div className="mt-6 text-xs opacity-60 text-right">
          Updated {new Date(data.generated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}
